/**
 * EditorCanvas — renders the layer stack to a 2D canvas and handles all direct
 * manipulation (select / drag / 8-handle resize / rotate) plus the grid and
 * safe-margin guides. Interaction math is rotation-aware: the pointer is mapped
 * into each layer's local frame so hit-testing and resizing work at any angle.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { toast } from '../../../utils/toast';
import { renderScene } from '../canvasRenderer';
import { RAW } from '../../../components/em/tokens';

const MIN = 8;
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const snap = (v, g, on) => (on ? Math.round(v / g) * g : v);

// local handle position for a w×h box centred at origin
const handleLocal = (h, w, ht) => ({
  nw: { x: -w / 2, y: -ht / 2 }, n: { x: 0, y: -ht / 2 }, ne: { x: w / 2, y: -ht / 2 },
  e: { x: w / 2, y: 0 }, se: { x: w / 2, y: ht / 2 }, s: { x: 0, y: ht / 2 },
  sw: { x: -w / 2, y: ht / 2 }, w: { x: -w / 2, y: 0 },
}[h]);
const OPPOSITE = { nw: 'se', n: 's', ne: 'sw', e: 'w', se: 'nw', s: 'n', sw: 'ne', w: 'e' };

export default function EditorCanvas({ state, actions, commit, imageCache }) {
  const { layers, canvasConfig, selectedLayerId } = state;
  const { width, height, zoom } = canvasConfig;
  const canvasRef = useRef(null);
  const drag = useRef(null); // active interaction

  const selected = layers.find((l) => l.id === selectedLayerId) || null;

  // ── draw ───────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * zoom * dpr;
    canvas.height = height * zoom * dpr;
    canvas.style.width = `${width * zoom}px`;
    canvas.style.height = `${height * zoom}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr * zoom, 0, 0, dpr * zoom, 0, 0);

    renderScene(ctx, { layers, canvasConfig }, imageCache);

    // Grid
    if (canvasConfig.showGrid) {
      const g = canvasConfig.gridSize;
      ctx.fillStyle = 'rgba(15,23,42,0.16)';
      for (let x = g; x < width; x += g)
        for (let y = g; y < height; y += g) ctx.fillRect(x - 0.5 / zoom, y - 0.5 / zoom, 1 / zoom, 1 / zoom);
    }
    // Safe margin
    if (canvasConfig.showSafeMargin) {
      const m = canvasConfig.safeMargin;
      ctx.save();
      ctx.strokeStyle = 'rgba(245,75,1,0.45)';
      ctx.lineWidth = 1 / zoom;
      ctx.setLineDash([6 / zoom, 4 / zoom]);
      ctx.strokeRect(m, m, width - 2 * m, height - 2 * m);
      ctx.restore();
    }
    // Selection overlay
    if (selected && selected.visible) {
      const s = selected;
      const cx = s.x + s.width / 2, cy = s.y + s.height / 2;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate((s.rotation * Math.PI) / 180);
      ctx.strokeStyle = RAW.orange;
      ctx.lineWidth = 1.5 / zoom;
      ctx.strokeRect(-s.width / 2, -s.height / 2, s.width, s.height);
      if (!s.locked) {
        const hs = 7 / zoom;
        ctx.fillStyle = '#ffffff';
        // rotate handle
        ctx.beginPath();
        ctx.moveTo(0, -s.height / 2);
        ctx.lineTo(0, -s.height / 2 - 22 / zoom);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(0, -s.height / 2 - 22 / zoom, hs * 0.9, 0, Math.PI * 2);
        ctx.fill(); ctx.stroke();
        HANDLES.forEach((h) => {
          const p = handleLocal(h, s.width, s.height);
          ctx.fillRect(p.x - hs / 2, p.y - hs / 2, hs, hs);
          ctx.strokeRect(p.x - hs / 2, p.y - hs / 2, hs, hs);
        });
      }
      ctx.restore();
    }
  }, [layers, canvasConfig, imageCache, selected, width, height, zoom]);

  useEffect(() => { draw(); }, [draw]);

  // ── geometry helpers ─────────────────────────────────────────────────────────
  const toLogical = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / zoom, y: (e.clientY - r.top) / zoom };
  };
  const toLocal = (p, layer) => {
    const cx = layer.x + layer.width / 2, cy = layer.y + layer.height / 2;
    const rad = (-layer.rotation * Math.PI) / 180;
    const dx = p.x - cx, dy = p.y - cy;
    return { x: dx * Math.cos(rad) - dy * Math.sin(rad), y: dx * Math.sin(rad) + dy * Math.cos(rad) };
  };
  const rot = (vx, vy, deg) => {
    const rad = (deg * Math.PI) / 180;
    return { x: vx * Math.cos(rad) - vy * Math.sin(rad), y: vx * Math.sin(rad) + vy * Math.cos(rad) };
  };

  const hitHandle = (p, layer) => {
    const lp = toLocal(p, layer);
    const tol = 9 / zoom;
    if (Math.abs(lp.x) < tol && Math.abs(lp.y - (-layer.height / 2 - 22 / zoom)) < tol) return 'rotate';
    for (const h of HANDLES) {
      const hl = handleLocal(h, layer.width, layer.height);
      if (Math.abs(lp.x - hl.x) < tol && Math.abs(lp.y - hl.y) < tol) return h;
    }
    return null;
  };
  const insideLayer = (p, layer) => {
    const lp = toLocal(p, layer);
    return Math.abs(lp.x) <= layer.width / 2 && Math.abs(lp.y) <= layer.height / 2;
  };

  // ── pointer interaction ──────────────────────────────────────────────────────
  const onPointerDown = (e) => {
    const p = toLogical(e);
    // 1) handle on the already-selected layer
    if (selected && !selected.locked) {
      const h = hitHandle(p, selected);
      if (h === 'rotate') {
        commit();
        drag.current = { mode: 'rotate', id: selected.id, cx: selected.x + selected.width / 2, cy: selected.y + selected.height / 2, startRot: selected.rotation };
        e.target.setPointerCapture?.(e.pointerId); return;
      }
      if (h) {
        commit();
        drag.current = { mode: 'resize', handle: h, id: selected.id, layer: { ...selected } };
        e.target.setPointerCapture?.(e.pointerId); return;
      }
    }
    // 2) pick top-most layer under the pointer
    let hit = null;
    for (let i = layers.length - 1; i >= 0; i--) {
      if (layers[i].visible && insideLayer(p, layers[i])) { hit = layers[i]; break; }
    }
    if (!hit) { actions.selectLayer(null); return; }
    actions.selectLayer(hit.id);
    if (hit.locked) { toast('Layer is locked'); return; }
    commit();
    drag.current = { mode: 'move', id: hit.id, start: p, origX: hit.x, origY: hit.y };
    e.target.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = toLogical(e);
    const on = canvasConfig.snapToGrid, g = canvasConfig.gridSize;

    if (d.mode === 'move') {
      actions.updateLayer(d.id, {
        x: snap(d.origX + (p.x - d.start.x), g, on),
        y: snap(d.origY + (p.y - d.start.y), g, on),
      });
    } else if (d.mode === 'rotate') {
      let deg = (Math.atan2(p.y - d.cy, p.x - d.cx) * 180) / Math.PI + 90;
      if (e.shiftKey) deg = Math.round(deg / 15) * 15;
      actions.updateLayer(d.id, { rotation: Math.round(deg) });
    } else if (d.mode === 'resize') {
      const L = d.layer;
      const c = { x: L.x + L.width / 2, y: L.y + L.height / 2 };
      const aL = handleLocal(OPPOSITE[d.handle], L.width, L.height);
      const rad = (-L.rotation * Math.PI) / 180;
      const dx = p.x - c.x, dy = p.y - c.y;
      const pL = { x: dx * Math.cos(rad) - dy * Math.sin(rad), y: dx * Math.sin(rad) + dy * Math.cos(rad) };
      const ax = ['e', 'w', 'ne', 'nw', 'se', 'sw'].includes(d.handle);
      const ay = ['n', 's', 'ne', 'nw', 'se', 'sw'].includes(d.handle);
      let newW = ax ? Math.max(MIN, Math.abs(pL.x - aL.x)) : L.width;
      let newH = ay ? Math.max(MIN, Math.abs(pL.y - aL.y)) : L.height;
      if (on) { if (ax) newW = Math.max(MIN, snap(newW, g, true)); if (ay) newH = Math.max(MIN, snap(newH, g, true)); }
      const ncx = ax ? (pL.x + aL.x) / 2 : 0;
      const ncy = ay ? (pL.y + aL.y) / 2 : 0;
      const off = rot(ncx, ncy, L.rotation);
      const cpx = c.x + off.x, cpy = c.y + off.y;
      const patch = { width: newW, height: newH, x: cpx - newW / 2, y: cpy - newH / 2 };
      if (L.type === 'name') patch.fontSize = Math.max(8, Math.round((L.fontSize || 24) * (newH / L.height)));
      actions.updateLayer(d.id, patch);
    }
  };

  const endDrag = (e) => {
    if (drag.current) { try { e.target.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ } }
    drag.current = null;
  };

  return (
    <canvas
      ref={canvasRef}
      className="pg-canvas"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={{ touchAction: 'none', display: 'block', boxShadow: '0 8px 30px -10px rgba(0,0,0,0.35)', borderRadius: 6 }}
    />
  );
}
